import { Asset } from 'app-lib/assetUtils'
import { DEX, IUniRoute } from 'app-lib/dexSelector/types'
import { BigNumber } from '@0x/utils'
import { ProviderType } from '../../domain/ProviderTypeDictionary'
import { PositionType } from 'bzx-common/src/domain/trade.types'

export enum UdfFeed {
  BINANCE = 'BINANCE',
  KYBER = 'KYBER',
}

export const udfFeeds: Record<UdfFeed, { id: UdfFeed; url: string; symbolSeparator: string }> = {
  BINANCE: { id: UdfFeed.BINANCE, url: 'https://api.ooki.com/udf/56', symbolSeparator: '' },
  KYBER: { id: UdfFeed.KYBER, url: 'https://api.kyber.network/chart', symbolSeparator: '_' },
}

export type ChainId = 1 | 10 | 56 | 137 | 42161

/**
 * Pairconfig example pseudo code for the WSTETH/ETH pair
 * ```
 * {
 *  WSTETH: {
 *    ETH:
 *      {dexes: [CURVE], disablePosition: {SHORT: true}, depositTokens: {LONG: [WSTETH]}}
 *    }
 *  }
 * }
 * ```
 */

export interface IPairSettings {
  dexes: DEX[]
  disabledPosition: Partial<Record<PositionType, boolean>>
  depositTokens?: Partial<Record<PositionType, Asset[]>>
  maxLeverage?: number // assuming long and short have same max for now
}

export type PairConfigs = Partial<Record<Asset, Partial<Record<Asset, IPairSettings>>>>

type NativeCurrency = {
  name: string
  symbol: string
  decimals: number
}

export interface NetworkDetails {
  blockExplorer: string
  blocksPerYear: number
  chainId: ChainId
  chainName: string
  shortName: string
  nativeCurrency: NativeCurrency
  rpcUrls?: Array<string>
}

export interface IChainConfig {
  /**
   * * `assets` are the ones that can be borrowed
   * * `collateralAssets` are the ones that can be used as collateral for a loan (borrow feature)
   */
  borrowing: { assets: Asset[]; collateralAssets: Asset[] }
  chainDetails: NetworkDetails
  chartUDF: { id: UdfFeed; url: string; symbolSeparator: string }
  comingSoon: {
    /** Allows to promote tokens that are going to be released but currently disabled in UI */
    tokens: Partial<Record<Asset, boolean>>
  }
  compromisedContracts: string[]
  contracts: {
    bzx: string
    helperProxy: string
    priceFeed: string
    sweepFees: string
    tokenRegistry: string
  }
  dexes: DEX[]
  /** Each chain swap implementation uses different dexes which are identified in dex records */
  dexesDetails: Partial<Record<DEX, { id: number; address: string }>>
  features: { dexSelector: boolean }
  gas: { priceDefault: BigNumber; amount: { fallback: number; coefficient: number } }
  icon: { name: string; color: string }
  lending: { assets: Asset[] }
  native: { asset: Asset; wrapped: Asset }
  /** Some blockchains work only with wrapped native token (WETH). eg: Arbitrum */
  nativeTokenSupported: boolean
  /**
   * Here are the different protocol versions with noticeable or breaking changes
   * * 2: first version of new interest rates
   * * 3: pay with ooki (getSwapExpectedReturn breaking change)
   * * 4: itoken as collateral (break in closeWithDeposit, closeWithSwap,
   *      getSwapExpectedReturn, getDepositAmountForBorrow, getBorrowAmountForDeposit)
   * * The frontend can currently run version 4 only.
   */
  protocol: { version: number }
  /** For blockchain read calls when user is not connected with his wallet */
  rpcUrlDefault: string
  stableCoins: Asset[]
  stablecoinForUsdRates: Asset
  stats: { assets: Asset[] }
  trading: {
    defaultPair: { baseToken: Asset; quoteToken: Asset }
    pairs: [Asset, Asset][]
    baseQuote: { base: Asset[]; quote: Asset[] }
    pairConfigs: PairConfigs
  }
  twitterHandle: string
  /** Disables UI for maintenance period, etc. */
  uiDisabled: boolean
  uiDisabledReason: string
  wallets: ProviderType[]
  uniRouteSettings: Record<string, IUniRoute[]>
}

/**
 * Get list of base and quote from the trade pairs list
 */
export function getBaseQuote(list: [Asset, Asset][]) {
  const indexedBase: Partial<Record<Asset, boolean>> = {}
  const indexedQuote: Partial<Record<Asset, boolean>> = {}
  return list.reduce(
    (acc, [base, quote]) => {
      if (!indexedBase[base]) {
        indexedBase[base] = true
        acc.base.push(base)
      }
      if (!indexedQuote[quote]) {
        indexedQuote[quote] = true
        acc.quote.push(quote)
      }
      return acc
    },
    { base: [], quote: [] } as { base: Asset[]; quote: Asset[] }
  )
}
